// Página de Términos de Uso del Sistema
import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileText, Calendar, ShieldCheck } from 'lucide-react'
import client from '../api/client'
import Card from '../components/ui/Card'

export default function TermsOfUse() {
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await client.get('/settings')
      return response.data?.datos || {}
    },
    staleTime: 5 * 60 * 1000,
  })

  const nombreSistema = settings?.nombre_sistema || 'SubiGol'
  const multiplicador = Number(settings?.multiplicador_goles_exacto || '3')
  const dividendoMinimo = settings?.dividendo_minimo_ganador || '1.05'

  return (
    <div className="py-8 px-4 sm:py-12 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-8 animate-fade-in font-body">
      {/* Encabezado */}
      <div className="flex items-center gap-4 border-b border-border-subtle pb-6">
        <div className="p-3 rounded-2xl bg-cyan/10 border border-cyan/20 text-cyan">
          <FileText className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-text-primary">
            Términos y Condiciones de Uso
          </h1>
          <p className="text-xs text-text-muted mt-1 flex items-center gap-1.5 font-mono">
            <Calendar className="w-3.5 h-3.5" /> Última actualización: 13 de junio de 2026
          </p>
        </div>
      </div>

      <div className="space-y-6 text-sm text-text-secondary leading-relaxed">
        <p>
          Bienvenido a <strong className="font-semibold text-text-primary">{nombreSistema}</strong>. Al acceder a nuestra plataforma, registrar una cuenta, realizar cargas de créditos, participar en las predicciones del Mundial de Fútbol 2026 o interactuar con nuestros servicios, usted acepta y se obliga a cumplir con los presentes Términos y Condiciones de Uso. Por favor, léalos detenidamente.
        </p>

        {/* Sección 1 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            1. Registro de Cuenta y Elegibilidad
          </h3>
          <p>
            Para utilizar los servicios de <strong className="font-semibold text-text-primary">{nombreSistema}</strong>, debe crear una cuenta de usuario proporcionando información verídica, exacta y actualizada (nombre, apellido y correo electrónico). Usted es responsable de la confidencialidad de su contraseña y de todas las actividades asociadas a su cuenta. Solo se permite una cuenta por persona. El uso de múltiples cuentas para manipular el ranking o los pozos de apuestas resultará en la suspensión inmediata del usuario.
          </p>
        </section>

        {/* Sección 2 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            2. Sistema de Pronósticos y Puntuación
          </h3>
          <p>
            La plataforma le permite ingresar predicciones para la Copa Mundial de la FIFA 2026. Los puntos se otorgan de acuerdo con las reglas de puntuación establecidas por el administrador (que definen recompensas para aciertos de primera y segunda posición de grupo, mejor tercero, y clasificación en rondas eliminatorias). Las fechas límites (cierre de pronósticos) se aplican de manera estricta y se configuran en la base de datos. Una vez cerrada la fecha límite, no se permitirán modificaciones.
          </p>
        </section>

        {/* Sección 3 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            3. Créditos, Depósitos y Retiros
          </h3>
          <p>
            <strong className="font-semibold text-text-primary">{nombreSistema}</strong> utiliza un sistema de créditos/saldos expresado en la moneda configurada (por ejemplo, Bolivianos - Bs) para participar en pozos de apuestas.
          </p>
          <ul className="list-disc list-inside pl-4 space-y-1">
            <li>
              <strong className="font-semibold text-text-primary">Cargas (Depósitos):</strong> Las recargas de saldo se solicitan subiendo un comprobante de depósito/transferencia a través del banco indicado en la plataforma. Estas solicitudes están sujetas a la verificación y aprobación manual del equipo de administración.
            </li>
            <li>
              <strong className="font-semibold text-text-primary">Retiros:</strong> Los usuarios pueden solicitar retiros de saldo subiendo el código QR de su cuenta bancaria. Los retiros se procesan de forma manual y requieren validación del estado por el administrador.
            </li>
            <li>
              <strong className="font-semibold text-text-primary">Matemática de Apuestas (Pozo Acumulado):</strong> Las apuestas se rigen bajo el formato Pari-mutuel. Las cuotas son indicativas y se resuelven deduciendo la comisión de la casa y distribuyendo el pozo neto entre los ganadores de la opción (con multiplicador x{multiplicador} para aciertos de goles exactos). Adicionalmente, se garantiza un dividendo de retorno mínimo de <strong className="font-semibold text-text-primary">{dividendoMinimo}x</strong> sobre las apuestas ganadoras. Si la distribución original resultara inferior a este dividendo, la comisión de la plataforma se reducirá automáticamente (pudiendo llegar al 0%), e incluso la plataforma inyectará un subsidio si el total del pozo no alcanza el valor mínimo garantizado. En caso de no haber ganadores en absoluto en el pozo, se aplicará el reembolso (parcial o total) según esté configurado por el administrador.
            </li>
          </ul>
        </section>


        {/* Sección 4 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            4. Limitación de Responsabilidad
          </h3>
          <p>
            <strong className="font-semibold text-text-primary">{nombreSistema}</strong> se proporciona "tal cual" y "según disponibilidad". No garantizamos que el servicio sea ininterrumpido, oportuno o libre de errores. No nos hacemos responsables de las pérdidas financieras sufridas por la participación en el sistema de apuestas, fallas en la red de comunicación externa, ni retrasos en los depósitos o retiros causados por entidades bancarias.
          </p>
        </section>

        {/* Sección 5 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            5. Modificaciones de los Términos
          </h3>
          <p>
            Nos reservamos el derecho de modificar estos términos en cualquier momento. Cualquier cambio significativo entrará en vigencia inmediatamente después de su publicación en esta página. Su uso continuo de los servicios de <strong className="font-semibold text-text-primary">{nombreSistema}</strong> tras dichos cambios constituirá su aceptación de los nuevos Términos y Condiciones de Uso.
          </p>
        </section>
      </div>

      {/* Sello de seguridad */}
      <Card className="p-4 bg-cyan/5 border border-cyan/15 rounded-xl flex items-center gap-3">
        <ShieldCheck className="w-6 h-6 text-cyan shrink-0" />
        <p className="text-xs text-text-muted leading-relaxed">
          Al utilizar la plataforma <strong className="font-semibold text-text-primary">{nombreSistema}</strong>, declaras cumplir con la mayoría de edad y poseer la capacidad legal necesaria para contratar y utilizar herramientas de entretenimiento digital conforme a la legislación aplicable en tu territorio.
        </p>
      </Card>
    </div>
  )
}
